/**
 * Planner Version History Manager
 * 
 * Tracks plan versions for undo/redo functionality in the Planner Agent.
 * Provides version control for presentation plans with:
 * - Automatic version creation after plan changes
 * - Restore to any previous version
 * - Version history for UI display
 * - Diff generation between versions
 * 
 * Currently uses in-memory storage. Will be extended with API persistence later.
 * 
 * Per Notion documentation:
 * https://www.notion.so/usecarousel/Planner-Agent-2bd1f0db4aec81eabc9ed5db68c3e0be
 */

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Type of edit that created the version */
export type EditType = 'user' | 'ai';

/** A single version in the history */
export interface PlanVersion {
    /** Sequential version number (1-indexed) */
    versionNumber: number;
    /** ISO timestamp when version was created */
    timestamp: string;
    /** Who/what made the change */
    editType: EditType;
    /** Complete plan snapshot */
    plan: unknown;
    /** Human-readable summary of changes */
    changesSummary: string;
}

/** Diff between two versions */
export interface VersionDiff {
    /** Section IDs that were added */
    sectionsAdded: string[];
    /** Section IDs that were removed */
    sectionsRemoved: string[];
    /** Slide IDs that were added */
    slidesAdded: string[];
    /** Slide IDs that were removed */
    slidesRemoved: string[];
    /** Count of content modifications */
    modificationsCount: number;
}

/** Plan structure for diff calculation */
interface PlanForDiff {
    sections: Array<{
        id: string;
        title?: string;
        purpose?: string;
        slides: Array<{
            id: string;
            title?: string;
            purpose?: string;
        }>;
    }>;
}

// ═══════════════════════════════════════════════════════════════════════════
// PLANNER VERSION HISTORY MANAGER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Manages version history for a planning job
 * 
 * @example
 * ```typescript
 * const manager = new PlannerVersionHistoryManager('job_abc123');
 * 
 * // Create initial version
 * manager.createVersion(plan, 'ai', 'Created initial plan with 45 slides');
 * 
 * // After edit, create new version
 * manager.createVersion(updatedPlan, 'ai', 'Added 3 slides to Section 2');
 * 
 * // Restore to previous version
 * const oldPlan = manager.restoreVersion(1);
 * 
 * // Get history for UI
 * const history = manager.getVersionHistory();
 * ```
 */
export class PlannerVersionHistoryManager {
    /** Job ID this manager is tracking */
    private readonly jobId: string;
    
    /** All versions (in-memory storage) */
    private versions: PlanVersion[] = [];
    
    /** Current version index (0-indexed into versions array) */
    private currentVersionIndex: number = -1;

    constructor(jobId: string) {
        this.jobId = jobId;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PUBLIC API
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Create a new version after plan changes
     * 
     * @param plan - The complete plan snapshot to save
     * @param editType - Who made the change ('user' or 'ai')
     * @param changesSummary - Human-readable description of changes
     * @returns The created version, or null if no changes from previous version
     */
    public createVersion(
        plan: unknown,
        editType: EditType,
        changesSummary: string
    ): PlanVersion | null {
        // Check if plan actually changed from the previous version
        if (this.versions.length > 0) {
            const previousPlan = this.versions[this.versions.length - 1].plan as PlanForDiff;
            const newPlan = plan as PlanForDiff;
            
            if (!this.hasChanges(previousPlan, newPlan)) {
                console.log(`[PlannerVersionHistoryManager] Skipping version creation - no changes from previous version`);
                return null;
            }
        }

        const version: PlanVersion = {
            versionNumber: this.versions.length + 1,
            timestamp: new Date().toISOString(),
            editType,
            plan: this.deepClone(plan),
            changesSummary
        };

        this.versions.push(version);
        this.currentVersionIndex = this.versions.length - 1;

        console.log(`[PlannerVersionHistoryManager] Created version ${version.versionNumber}: ${changesSummary}`);

        return version;
    }

    /**
     * Restore plan to a specific version
     * 
     * Creates a new version from the restored plan (branching, not truncating history).
     * 
     * @param versionNumber - The version number to restore (1-indexed)
     * @returns The restored plan
     * @throws Error if version number is invalid
     */
    public restoreVersion(versionNumber: number): unknown {
        if (versionNumber < 1 || versionNumber > this.versions.length) {
            throw new Error(`Invalid version number: ${versionNumber}. Must be between 1 and ${this.versions.length}`);
        }

        const targetVersion = this.versions[versionNumber - 1];
        const restoredPlan = this.deepClone(targetVersion.plan);

        // Create new version from the restored plan (branching)
        this.createVersion(
            restoredPlan,
            'user',
            `Restored to version ${versionNumber}`
        );

        console.log(`[PlannerVersionHistoryManager] Restored to version ${versionNumber}`);

        return restoredPlan;
    }

    /**
     * Get all versions for UI display
     * 
     * @returns Array of all versions, newest first
     */
    public getVersionHistory(): PlanVersion[] {
        // Return reversed copy (newest first) for UI display
        return [...this.versions].reverse();
    }

    /**
     * Get the current version
     * 
     * @returns The current version, or null if no versions exist
     */
    public getCurrentVersion(): PlanVersion | null {
        if (this.currentVersionIndex < 0 || this.versions.length === 0) {
            return null;
        }
        return this.versions[this.currentVersionIndex];
    }

    /**
     * Get the current version number
     * 
     * @returns Current version number (1-indexed), or 0 if no versions
     */
    public getCurrentVersionNumber(): number {
        return this.currentVersionIndex >= 0 ? this.currentVersionIndex + 1 : 0;
    }

    /**
     * Get a specific version by number
     * 
     * @param versionNumber - The version number (1-indexed)
     * @returns The version, or null if not found
     */
    public getVersion(versionNumber: number): PlanVersion | null {
        if (versionNumber < 1 || versionNumber > this.versions.length) {
            return null;
        }
        return this.versions[versionNumber - 1];
    }

    /**
     * Get total number of versions
     */
    public getVersionCount(): number {
        return this.versions.length;
    }

    /**
     * Generate diff between two versions
     * 
     * @param fromVersion - Source version number (1-indexed)
     * @param toVersion - Target version number (1-indexed)
     * @returns Diff showing what changed between versions
     * @throws Error if version numbers are invalid
     */
    public getDiff(fromVersion: number, toVersion: number): VersionDiff {
        if (fromVersion < 1 || fromVersion > this.versions.length) {
            throw new Error(`Invalid from version: ${fromVersion}`);
        }
        if (toVersion < 1 || toVersion > this.versions.length) {
            throw new Error(`Invalid to version: ${toVersion}`);
        }

        const fromPlan = this.versions[fromVersion - 1].plan as PlanForDiff;
        const toPlan = this.versions[toVersion - 1].plan as PlanForDiff;

        return this.calculateDiff(fromPlan, toPlan);
    }

    /**
     * Clear all versions (reset)
     */
    public clear(): void {
        this.versions = [];
        this.currentVersionIndex = -1;
        console.log(`[PlannerVersionHistoryManager] Cleared all versions for job ${this.jobId}`);
    }

    /**
     * Get the job ID this manager is tracking
     */
    public getJobId(): string {
        return this.jobId;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PRIVATE HELPERS
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Deep clone an object to prevent mutations
     */
    private deepClone<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    /**
     * Check if two plans have any meaningful changes
     * 
     * @param fromPlan - The previous plan
     * @param toPlan - The new plan
     * @returns true if there are changes, false if plans are identical
     */
    private hasChanges(fromPlan: PlanForDiff, toPlan: PlanForDiff): boolean {
        const diff = this.calculateDiff(fromPlan, toPlan);
        
        // Check if any changes exist
        return (
            diff.sectionsAdded.length > 0 ||
            diff.sectionsRemoved.length > 0 ||
            diff.slidesAdded.length > 0 ||
            diff.slidesRemoved.length > 0 ||
            diff.modificationsCount > 0
        );
    }

    /**
     * Calculate diff between two plans
     */
    private calculateDiff(fromPlan: PlanForDiff, toPlan: PlanForDiff): VersionDiff {
        const fromSectionIds = new Set(fromPlan.sections?.map(s => s.id) || []);
        const toSectionIds = new Set(toPlan.sections?.map(s => s.id) || []);

        const fromSlideIds = new Set(
            fromPlan.sections?.flatMap(s => s.slides?.map(sl => sl.id) || []) || []
        );
        const toSlideIds = new Set(
            toPlan.sections?.flatMap(s => s.slides?.map(sl => sl.id) || []) || []
        );

        // Find added/removed sections
        const sectionsAdded = [...toSectionIds].filter(id => !fromSectionIds.has(id));
        const sectionsRemoved = [...fromSectionIds].filter(id => !toSectionIds.has(id));

        // Find added/removed slides
        const slidesAdded = [...toSlideIds].filter(id => !fromSlideIds.has(id));
        const slidesRemoved = [...fromSlideIds].filter(id => !toSlideIds.has(id));

        // Count modifications (items that exist in both but may have changed)
        let modificationsCount = 0;

        // Check sections that exist in both
        for (const sectionId of fromSectionIds) {
            if (toSectionIds.has(sectionId)) {
                const fromSection = fromPlan.sections.find(s => s.id === sectionId);
                const toSection = toPlan.sections.find(s => s.id === sectionId);
                
                if (fromSection && toSection) {
                    if (fromSection.title !== toSection.title || 
                        fromSection.purpose !== toSection.purpose) {
                        modificationsCount++;
                    }
                }
            }
        }

        // Check slides that exist in both
        for (const slideId of fromSlideIds) {
            if (toSlideIds.has(slideId)) {
                const fromSlide = fromPlan.sections
                    .flatMap(s => s.slides || [])
                    .find(sl => sl.id === slideId);
                const toSlide = toPlan.sections
                    .flatMap(s => s.slides || [])
                    .find(sl => sl.id === slideId);
                
                if (fromSlide && toSlide) {
                    if (fromSlide.title !== toSlide.title || 
                        fromSlide.purpose !== toSlide.purpose) {
                        modificationsCount++;
                    }
                }
            }
        }

        return {
            sectionsAdded,
            sectionsRemoved,
            slidesAdded,
            slidesRemoved,
            modificationsCount
        };
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

/** Map of job IDs to their version history managers */
const managers = new Map<string, PlannerVersionHistoryManager>();

/**
 * Get or create a PlannerVersionHistoryManager for a job
 * 
 * @param jobId - The job ID to get/create manager for
 * @returns The PlannerVersionHistoryManager instance
 */
export function getPlannerVersionHistoryManager(jobId: string): PlannerVersionHistoryManager {
    let manager = managers.get(jobId);
    if (!manager) {
        manager = new PlannerVersionHistoryManager(jobId);
        managers.set(jobId, manager);
    }
    return manager;
}

/**
 * Remove a PlannerVersionHistoryManager for a job (cleanup)
 * 
 * @param jobId - The job ID to remove
 */
export function removePlannerVersionHistoryManager(jobId: string): void {
    managers.delete(jobId);
}

/**
 * Get all active job IDs with version history
 */
export function getActiveJobIds(): string[] {
    return [...managers.keys()];
}


